import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { ActivatedRoute, Router } from "@angular/router";
import { MatIconModule } from "@angular/material/icon";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, firstValueFrom, of, switchMap } from "rxjs";
import { ApiService } from "../core/api.service";
import { resizeCatalogImage } from "../core/image.util";
import { BlindCategory, CatalogBlind } from "../core/models";

const NORMALIZE_TAGS = (raw: string): string[] =>
  [
    ...new Set(
      raw
        .split(",")
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean),
    ),
  ];

const TAGS_TO_STRING = (tags: string[] | undefined): string =>
  tags?.join(", ") ?? "";

@Component({
  selector: "app-blind-detail-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterModule, MatIconModule],
  template: `
    <section class="dashboard">
      <div class="dashboard-main">
        <header class="dashboard-header">
          <div>
            <h1>{{ blind()?.name ?? "Blind" }}</h1>
            @if (blind(); as b) {
              <p class="header-subtitle">
                {{ b.categoryName ?? "Uncategorized" }}
              </p>
            }
          </div>
          <div class="header-actions">
            <a class="action-button" routerLink="/catalog/blinds">← Back</a>
          </div>
        </header>

        @if (loadError()) {
          <div class="panel-card">
            <p class="empty-message">{{ loadError() }}</p>
          </div>
        } @else if (blind()) {
          <div class="panel-card">
            <div class="panel-header">
              <h2>Edit blind</h2>
            </div>
            <div class="panel-content">
              <div class="blind-form">
                <div class="form-grid">
                  <div class="form-field-group">
                    <label>Name</label>
                    <input class="form-input" [(ngModel)]="blindForm.name" placeholder="e.g. Ribbon" />
                  </div>
                  <div class="form-field-group">
                    <label>Semi-blackout code</label>
                    <input class="form-input" [(ngModel)]="blindForm.semiBlackoutCode" placeholder="e.g. RB-100S" />
                  </div>
                  <div class="form-field-group">
                    <label>Blackout code</label>
                    <input class="form-input" [(ngModel)]="blindForm.blackoutCode" placeholder="e.g. RB-100B" />
                  </div>
                  <div class="form-field-group">
                    <label>Category</label>
                    <select class="form-select" [(ngModel)]="blindForm.categoryId">
                      <option value="">-- Select category --</option>
                      @for (cat of categories(); track cat.id) {
                        <option [value]="cat.id">{{ cat.name }}</option>
                      }
                    </select>
                  </div>
                  <div class="form-field-group">
                    <label>Tags</label>
                    <input
                      class="form-input"
                      [(ngModel)]="blindForm.tags"
                      placeholder="e.g. beige, warm tones, linen"
                    />
                    <span class="field-hint">Comma-separated · used for customer search</span>
                  </div>
                  <div class="form-field-group">
                    <label>Supplier</label>
                    <select class="form-select" [(ngModel)]="blindForm.supplierId">
                      <option value="">-- Select supplier --</option>
                      @for (s of suppliers(); track s.id) {
                        <option [value]="s.id">{{ s.name }}</option>
                      }
                    </select>
                    <span class="field-hint">Required before the blind shows to customers</span>
                  </div>
                  <div class="form-field-group checkbox-field">
                    <label class="checkbox-label">
                      <input type="checkbox" [(ngModel)]="blindForm.favorite" />
                      Featured on home page
                    </label>
                  </div>
                  <div class="form-field-group checkbox-field">
                    <label class="checkbox-label">
                      <input type="checkbox" [(ngModel)]="blindForm.visible" />
                      Visible to customers
                    </label>
                  </div>
                </div>
                <div class="form-field-group image-field">
                  <label>Image</label>
                  <div
                    class="image-dropzone"
                    [class.dragging]="isDragging()"
                    (click)="imageInput.click()"
                    (dragover)="onDragOver($event)"
                    (dragleave)="onDragLeave()"
                    (drop)="onDrop($event)"
                  >
                    <input
                      #imageInput
                      type="file"
                      accept="image/*"
                      hidden
                      (change)="onImageSelected($event)"
                    />
                    @if (imagePreview()) {
                      <img class="dropzone-preview" [src]="imagePreview()" alt="Blind preview" />
                      <div class="dropzone-overlay">
                        <span class="dropzone-overlay-btn">Change</span>
                        <span class="dropzone-overlay-btn danger" (click)="clearImage($event)">Remove</span>
                      </div>
                    } @else {
                      <div class="dropzone-placeholder">
                        <mat-icon class="dropzone-icon">add_photo_alternate</mat-icon>
                        <span class="dropzone-title">Upload blind image</span>
                        <span class="dropzone-hint">Click or drag &amp; drop · JPG or PNG</span>
                      </div>
                    }
                  </div>
                </div>
                <div class="actions">
                  <button class="action-button primary" (click)="saveBlind()" [disabled]="saving()">
                    {{ saving() ? "Saving..." : "Save changes" }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="panel-card">
            <div class="panel-header">
              <h2>Lifestyle photos</h2>
              <p class="panel-description">
                {{ committedGallery().length }} committed ·
                {{ galleryImages().length }} in draft
              </p>
            </div>
            <div class="panel-content">
              @if (galleryStatus() === "generating") {
                <p class="gallery-status">Generating… {{ galleryImages().length }}/8</p>
              }
              @if (galleryError()) {
                <p class="gallery-error">{{ galleryError() }}</p>
              }
              @if (galleryImages().length === 0 && galleryStatus() !== "generating") {
                <div class="gallery-empty">
                  <p class="empty-message">No lifestyle photos yet.</p>
                  @if (blind()?.imageUrl) {
                    <button
                      class="action-button primary"
                      (click)="startGallery()"
                      [disabled]="galleryBusy()"
                    >
                      Generate lifestyle photos
                    </button>
                  } @else {
                    <p class="empty-message">Upload a blind photo first, then generate.</p>
                  }
                </div>
              }
              @if (galleryImages().length > 0) {
                <div class="gallery-grid">
                  @for (img of galleryImages(); track img.url; let i = $index) {
                    <div class="gallery-slot">
                      <img [src]="img.url" [alt]="'Lifestyle photo ' + (i + 1)" />
                      <button class="chip-btn" (click)="regenerate(i)" title="Regenerate">↻</button>
                    </div>
                  }
                </div>
              }
              @if (galleryImages().length > 0 && galleryStatus() === "ready") {
                <div class="actions">
                  <button class="action-button primary" (click)="saveGallery()" [disabled]="galleryBusy()">
                    Save gallery
                  </button>
                  <button class="action-button" (click)="cancelGallery()">Cancel</button>
                </div>
              }
            </div>
          </div>

          @if (message()) {
            <div class="status-message">{{ message() }}</div>
          }
        }
      </div>
    </section>
  `,
  styles: [
    `
      .dashboard { display: grid; grid-template-columns: 1fr; gap: 24px; align-items: start; }
      .dashboard-main { min-width: 0; }
      .dashboard-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 16px; }
      .dashboard-header h1 { font-size: 32px; margin: 0; }
      .header-subtitle { color: var(--ink-500); margin: 4px 0 0; font-size: 14px; }
      .header-actions { display: flex; gap: 10px; align-items: center; }
      .action-button {
        border: none; background: var(--panel); color: var(--ink-900); padding: 10px 16px;
        border-radius: 12px; font-weight: 600; font-size: 14px; cursor: pointer;
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08); text-decoration: none;
        display: inline-flex; align-items: center;
      }
      .action-button.primary { background: #111827; color: #ffffff; box-shadow: 0 8px 20px rgba(17, 24, 39, 0.15); }
      .action-button:disabled { opacity: 0.5; cursor: not-allowed; }
      .panel-card { background: var(--panel); border-radius: 20px; padding: 24px; box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1); margin-bottom: 24px; }
      .panel-header { margin-bottom: 18px; }
      .panel-header h2 { font-size: 18px; margin: 0; color: var(--ink-900); }
      .panel-description { font-size: 13px; color: var(--ink-500); margin: 4px 0 0; }
      .panel-content { display: grid; gap: 12px; }
      .form-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); margin-bottom: 12px; }
      .form-field-group { display: flex; flex-direction: column; gap: 6px; }
      .form-field-group label { font-size: 13px; font-weight: 600; color: var(--ink-900); }
      .field-hint { font-size: 11px; color: var(--ink-500); }
      .checkbox-field { justify-content: center; }
      .checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 600; color: var(--ink-900); cursor: pointer; padding-top: 24px; }
      .checkbox-label input { width: 16px; height: 16px; accent-color: #111827; }
      .form-select, .form-input { padding: 10px 12px; border: 1px solid rgba(15, 23, 42, 0.12); border-radius: 10px; font-size: 14px; font-family: inherit; color: var(--ink-900); background: #fafbfc; }
      .form-select:focus, .form-input:focus { outline: none; border-color: #111827; background: #ffffff; box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.08); }
      .blind-form { border: 1px solid rgba(15,23,42,0.1); border-radius: 12px; padding: 16px; background: #fafbfc; }
      .image-field { margin-top: 4px; }
      .image-dropzone { position: relative; border: 2px dashed rgba(15, 23, 42, 0.18); border-radius: 12px; min-height: 150px; display: flex; align-items: center; justify-content: center; cursor: pointer; overflow: hidden; background: #fafbfc; transition: border-color 0.2s ease, background 0.2s ease; }
      .image-dropzone.dragging, .image-dropzone:hover { border-color: rgba(17, 24, 39, 0.6); background: #ffffff; }
      .dropzone-placeholder { display: flex; flex-direction: column; align-items: center; gap: 6px; padding: 24px; text-align: center; font-size: 13px; color: var(--ink-500); }
      .dropzone-icon { font-size: 32px; width: 32px; height: 32px; line-height: 32px; color: var(--ink-500); }
      .dropzone-title { font-weight: 600; color: var(--ink-900); font-size: 14px; }
      .dropzone-hint { font-size: 12px; color: var(--ink-500); opacity: 0.8; }
      .dropzone-preview { width: 100%; height: 150px; object-fit: cover; display: block; }
      .dropzone-overlay { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; gap: 8px; background: rgba(15, 23, 42, 0.45); opacity: 0; transition: opacity 0.2s ease; }
      .image-dropzone:hover .dropzone-overlay { opacity: 1; }
      .dropzone-overlay-btn { background: #ffffff; color: var(--ink-900); font-size: 12px; font-weight: 600; padding: 7px 14px; border-radius: 10px; cursor: pointer; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.18); }
      .dropzone-overlay-btn.danger { color: #dc2626; }
      .actions { display: flex; gap: 8px; margin-top: 12px; }
      .gallery-status { font-size: 13px; color: var(--ink-500); }
      .gallery-error { font-size: 13px; color: #dc2626; }
      .gallery-empty { display: flex; flex-direction: column; gap: 12px; align-items: flex-start; }
      .gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: 12px; }
      .gallery-slot { position: relative; border: 1px solid rgba(15,23,42,0.1); border-radius: 10px; overflow: hidden; background: #fff; }
      .gallery-slot img { width: 100%; height: 130px; object-fit: cover; display: block; }
      .gallery-slot .chip-btn { position: absolute; top: 6px; right: 6px; background: rgba(255,255,255,0.9); border-radius: 6px; }
      .chip-btn { border: none; background: transparent; cursor: pointer; color: var(--ink-500); font-size: 13px; padding: 2px 4px; }
      .chip-btn:hover { background: rgba(15,23,42,0.06); border-radius: 6px; }
      .empty-message { color: var(--ink-500); font-size: 13px; }
      .status-message { color: var(--ink-500); font-size: 13px; padding: 12px; background: #fef3c7; border-radius: 10px; margin-top: 12px; }
    `,
  ],
})
export class BlindDetailPageComponent implements OnDestroy {
  private readonly api = inject(ApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly blindId = this.route.snapshot.paramMap.get("id") ?? "";

  readonly saving = signal(false);
  readonly message = signal("");
  readonly isDragging = signal(false);
  readonly loadError = signal("");

  readonly blindResource = rxResource({
    stream: () =>
      this.route.paramMap.pipe(
        switchMap((params) => {
          const id = params.get("id") ?? "";
          return id
            ? this.api.getCatalogBlind(id).pipe(catchError((err) => {
                this.loadError.set(err?.error?.message ?? "Failed to load blind.");
                return of(null as unknown as CatalogBlind);
              }))
            : of(null as unknown as CatalogBlind);
        }),
      ),
  });
  readonly blind = this.blindResource.value;

  readonly categoriesResource = rxResource({
    stream: () =>
      this.api.getCategories().pipe(catchError(() => of([] as BlindCategory[]))),
  });
  readonly categories = this.categoriesResource.value;

  readonly suppliersResource = rxResource({
    stream: () =>
      this.api
        .listSuppliers()
        .pipe(catchError(() => of([] as { id: string; name: string }[]))),
  });
  readonly suppliers = this.suppliersResource.value;

  readonly committedGallery = computed(
    () => this.blind()?.gallery ?? ([] as string[]),
  );

  // Edit form state
  blindForm = {
    name: "",
    semiBlackoutCode: "",
    blackoutCode: "",
    categoryId: "",
    tags: "",
    favorite: false,
    supplierId: "",
    visible: true,
  };
  imagePreviewSignal = signal<string | null>(null);
  selectedImage: File | null = null;

  // Gallery state
  readonly galleryImages = signal<{ url: string; scene: string }[]>([]);
  readonly galleryStatus = signal<string>("idle");
  readonly galleryError = signal<string>("");
  readonly galleryBusy = signal(false);
  private galleryTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    // Fill the form + seed gallery when the blind resource loads. `effect`
    // reads blind(); the actual writes are deferred via queueMicrotask so
    // signal writes happen OUTSIDE the reactive context (avoids NG0600).
    effect(() => {
      const b = this.blind();
      if (!b) return;
      queueMicrotask(() => this.fillForm(b));
    });
  }

  private fillForm(b: CatalogBlind) {
    this.blindForm = {
      name: b.name,
      semiBlackoutCode: b.semiBlackoutCode,
      blackoutCode: b.blackoutCode,
      categoryId: b.categoryId ?? "",
      tags: TAGS_TO_STRING(b.tags),
      favorite: b.isFavorite ?? false,
      supplierId: b.supplierId ?? "",
      visible: !b.hidden,
    };
    this.imagePreviewSignal.set(b.imageUrl ?? null);
    if (b.galleryDraft && b.galleryDraft.images.length) {
      this.galleryImages.set(b.galleryDraft.images.map((i) => ({ url: i.url, scene: i.scene })));
      this.galleryStatus.set(b.galleryDraft.status);
      if (b.galleryDraft.status === "generating") this.startPolling(this.blindId);
    }
    this.cdr.markForCheck();
  }

  imagePreview() {
    return this.imagePreviewSignal();
  }

  onImageSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.setImageFile(file);
    input.value = "";
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragging.set(true);
  }

  onDragLeave() {
    this.isDragging.set(false);
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragging.set(false);
    const file = event.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      this.setImageFile(file);
    } else if (file) {
      this.message.set("Please drop an image file.");
    }
  }

  clearImage(event: Event) {
    event.stopPropagation();
    this.selectedImage = null;
    this.imagePreviewSignal.set(null);
  }

  private setImageFile(file: File) {
    if (!file.type.startsWith("image/")) {
      this.message.set("Please choose an image file.");
      return;
    }
    this.selectedImage = file;
    const reader = new FileReader();
    reader.onload = () => this.imagePreviewSignal.set(reader.result as string);
    reader.readAsDataURL(file);
  }

  async saveBlind() {
    const name = this.blindForm.name.trim();
    const semiBlackoutCode = this.blindForm.semiBlackoutCode.trim();
    const blackoutCode = this.blindForm.blackoutCode.trim();
    if (!name || !semiBlackoutCode || !blackoutCode) {
      this.message.set("Name, semi-blackout code, and blackout code are required.");
      return;
    }
    this.saving.set(true);
    try {
      let imageKey: string | undefined;
      let imageUrl: string | undefined;
      if (this.selectedImage) {
        // Crop to 4:3 and cap at 4K (never upscale) so it fits the customer
        // detail page and zooms cleanly.
        const upload = await resizeCatalogImage(this.selectedImage);
        const presigned = await firstValueFrom(
          this.api.presignCatalogImage(upload.type, upload.name),
        );
        const resp = await fetch(presigned.uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": upload.type },
          body: upload,
        });
        if (!resp.ok) throw new Error("Image upload failed");
        imageKey = presigned.objectKey;
        imageUrl = presigned.publicUrl;
      }

      const payload = {
        name,
        semiBlackoutCode,
        blackoutCode,
        categoryId: this.blindForm.categoryId || undefined,
        isFavorite: this.blindForm.favorite,
        tags: NORMALIZE_TAGS(this.blindForm.tags),
        supplierId: this.blindForm.supplierId || undefined,
        hidden: !this.blindForm.visible,
        imageKey,
        imageUrl,
      };

      const updated = await firstValueFrom(
        this.api.updateBlind(this.blindId, payload),
      );
      this.blindResource.update(() => updated);
      this.message.set("Blind updated.");
    } catch (err: any) {
      this.message.set(err?.message ?? "Failed to save blind.");
    } finally {
      this.saving.set(false);
    }
  }

  private stopPolling() {
    if (this.galleryTimer) {
      clearInterval(this.galleryTimer);
      this.galleryTimer = null;
    }
  }

  private startPolling(id: string) {
    this.stopPolling();
    this.galleryTimer = setInterval(() => void this.checkGallery(id), 3000);
  }

  private async checkGallery(id: string) {
    try {
      const s = await firstValueFrom(this.api.galleryStatus(id));
      if (s.images) this.galleryImages.set(s.images);
      if (s.status === "ready") {
        this.galleryStatus.set("ready");
        this.galleryBusy.set(false);
        this.stopPolling();
      } else if (s.status === "error") {
        this.galleryError.set(s.error ?? "Gallery generation failed.");
        this.galleryStatus.set("error");
        this.galleryBusy.set(false);
        this.stopPolling();
      } else {
        this.galleryStatus.set(s.status);
      }
    } catch (err: any) {
      this.galleryError.set(err?.error?.message ?? "Failed to check gallery status.");
      this.galleryStatus.set("error");
      this.galleryBusy.set(false);
      this.stopPolling();
    }
  }

  async startGallery() {
    if (!this.blindId) return;
    this.galleryImages.set([]);
    this.galleryError.set("");
    this.galleryStatus.set("generating");
    this.galleryBusy.set(true);
    try {
      await firstValueFrom(this.api.generateGallery(this.blindId));
      this.startPolling(this.blindId);
    } catch (err: any) {
      this.galleryError.set(err?.error?.message ?? "Failed to start gallery generation.");
      this.galleryStatus.set("error");
      this.galleryBusy.set(false);
    }
  }

  async regenerate(index: number) {
    if (!this.blindId) return;
    this.galleryError.set("");
    this.galleryStatus.set("generating");
    this.galleryBusy.set(true);
    try {
      await firstValueFrom(this.api.regenerateGallery(this.blindId, index));
      this.startPolling(this.blindId);
    } catch (err: any) {
      this.galleryError.set(err?.error?.message ?? "Failed to regenerate photo.");
      this.galleryStatus.set("error");
      this.galleryBusy.set(false);
    }
  }

  private closeGallery() {
    this.stopPolling();
    this.galleryImages.set([]);
    this.galleryStatus.set("idle");
    this.galleryError.set("");
    this.galleryBusy.set(false);
  }

  async saveGallery() {
    if (!this.blindId) return;
    try {
      const saved = await firstValueFrom(this.api.saveGallery(this.blindId));
      this.blindResource.update(() => saved);
      this.message.set("Gallery saved.");
    } catch (err: any) {
      this.message.set(err?.error?.message ?? "Failed to save gallery.");
    } finally {
      this.closeGallery();
    }
  }

  async cancelGallery() {
    if (!this.blindId) return;
    this.closeGallery();
    try {
      await firstValueFrom(this.api.cancelGallery(this.blindId));
    } catch {
      // Ignore.
    }
  }

  ngOnDestroy() {
    this.stopPolling();
  }
}
